import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiShield } from 'react-icons/fi';

const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Back link */}
        <Link 
          to="/" 
          className="inline-flex items-center text-gray-600 hover:text-primary-600 mb-8"
        >
          <FiArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-gray-600">Last updated: August 2025</p>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <div className="bg-white rounded-lg shadow-sm p-8 space-y-6">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Welcome to AnonLink</h2>
              <p className="text-gray-700">
                By using AnonLink, you agree to these terms. We've kept them short and clear.
                If you don't agree with any part, please don't use our service.
              </p>
            </section>

            {/* Acceptable Use */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Acceptable Use</h2>
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <p className="text-gray-700 font-medium">
                  AnonLink must NOT be used for:
                </p>
                <ul className="list-disc list-inside mt-2 text-gray-700">
                  <li>Illegal activities or content</li>
                  <li>Malware, phishing, or harmful software distribution</li>
                  <li>Harassment, hate speech, or threats</li>
                  <li>Copyright infringement or piracy</li>
                  <li>Spam or deceptive practices</li>
                  <li>Any activity that harms others or violates their rights</li>
                </ul>
              </div>
              <p className="text-gray-700">
                We will terminate accounts and report illegal activities to authorities when required by law.
              </p>
            </section>

            {/* Your Account */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Your Account</h2>
              <ul className="space-y-2 text-gray-700">
                <li>• You're responsible for your account security</li>
                <li>• One person per account (no sharing)</li>
                <li>• You must provide accurate information (we don't ask for much info)</li>
                <li>• We can suspend or terminate accounts that violate these terms</li>
              </ul>
            </section>

            {/* The Service */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">The Service</h2>
              <ul className="space-y-2 text-gray-700">
                <li>• AnonLink shortens URLs and protects privacy</li>
                <li>• Free accounts have usage limits (10 links/month)</li>
                <li>• Pro accounts ($2.99/month) get unlimited links</li>
                <li>• We may change features or pricing with 30 days notice</li>
                <li>• Service is provided "as is" without warranties</li>
              </ul>
            </section>

            {/* Privacy First */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Privacy First</h2>
              <div className="bg-green-50 border-l-4 border-green-500 p-4">
                <p className="text-gray-700">
                  We're committed to privacy:
                </p>
                <ul className="list-disc list-inside mt-2 text-gray-700">
                  <li>No tracking of users</li>
                  <li>No selling of data</li>
                  <li>Minimal data collection</li>
                  <li>See our <Link to="/privacy" className="text-primary-600 hover:text-primary-700">Privacy Policy</Link> for details</li>
                </ul>
              </div>
            </section>

            {/* Payments */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Payments</h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Pro subscriptions are billed monthly</li>
                <li>• Cancel anytime - you keep access until the end of your billing period</li>
                <li>• Payments are processed securely by Stripe</li>
                <li>• No refunds for partial months</li>
              </ul>
            </section>

            {/* Liability */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Limitation of Liability</h2>
              <p className="text-gray-700">
                AnonLink is not liable for:
              </p>
              <ul className="list-disc list-inside mt-2 text-gray-700">
                <li>Content accessed through shortened links</li>
                <li>Service interruptions or data loss</li>
                <li>Indirect or consequential damages</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Our total liability is limited to the amount you've paid us in the past 12 months.
              </p>
            </section>

            {/* Changes */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Changes to Terms</h2>
              <p className="text-gray-700">
                We may update these terms. If changes are significant, we'll notify you via email 
                or through the service. Continued use means you accept the new terms.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Contact</h2>
              <p className="text-gray-700">
                Questions about these terms? Email us at{' '}
                <a href="mailto:legal@anonlink.com" className="text-primary-600 hover:text-primary-700">
                  legal@anonlink.com
                </a>
              </p>
            </section>

            {/* Jurisdiction */}
            <section className="border-t pt-6">
              <p className="text-sm text-gray-600">
                These terms are governed by the laws of the United States. 
                Any disputes will be resolved in the courts of Wyoming.
              </p>
            </section>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="mt-12 text-center">
          <Link 
            to="/register" 
            className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <FiShield className="w-5 h-5 mr-2" />
            Get Started with AnonLink
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;